import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ordo/ui/tooltip'
import { Info } from 'lucide-react'
import type { ReactElement } from 'react'

import { formatCents } from '@/lib/school-orders/money'

/**
 * The school checkout "Est. tax" row.
 *
 * School bulk orders ARE billed campus-rate sales tax — just at invoicing,
 * not at submit. The row used to show a hard `$0.00`, which was true of the
 * submit and false of the bill, so a school admin could reasonably expect the
 * invoice to match the checkout total and be surprised.
 *
 * So the row shows the ESTIMATE at the campus's current rate and says, in the
 * tooltip, that the real number is worked out at invoice time and can differ.
 * It can differ for real reasons: the invoice resolves tax per line against
 * the rate in force on the SERVICE date and against any governed exemption,
 * neither of which is known at checkout.
 *
 * Shared by the checkout summary and the right-rail cart so the wording, the
 * affordance, and the rounding are identical — a school admin comparing the
 * two must not see two different explanations of the same number.
 *
 * This is DISPLAY ONLY. The insufficient-funds gate deliberately keeps using
 * the submit-time figure (`schoolOrderExpectedDebitCents`, which is untaxed);
 * gating on an estimate would refuse an order the school can actually afford.
 */
export const ESTIMATED_TAX_TOOLTIP =
  'Sales tax on bulk orders is calculated when your invoice is generated, at your campus rate on each service date. This estimate may differ from the final amount.'

export function EstimatedTaxRow({
  estimatedTaxCents,
  currency,
  className,
}: {
  /** Null when no line is priced — the row then reads "—", mirroring subtotal. */
  estimatedTaxCents: number | null
  currency: string
  className?: string
}): ReactElement {
  return (
    <div className={className ?? 'flex items-baseline justify-between gap-2'}>
      <dt className="flex items-center gap-1 text-muted-foreground">
        Est. tax
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* A button, not a bare icon: the explanation has to be reachable
                  by keyboard and to screen readers, not hover-only. */}
              <button
                type="button"
                aria-label="Why is this an estimate?"
                className="inline-flex items-center text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none">
                <Info className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[16rem] text-pretty">{ESTIMATED_TAX_TOOLTIP}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </dt>
      <dd className="tabular-nums text-foreground">
        {estimatedTaxCents !== null ? formatCents(estimatedTaxCents, currency) : '—'}
      </dd>
    </div>
  )
}
